import { hasPremiumFeature } from '@lub0v/santa-utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxTheme, type UserAccountInfo } from '@lub0v/santa-utils';
import { AppearancePicker } from '@src/pages/NewBoxPage/AppearancePicker';
import { santaClient } from '@src/state/client';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';
import { resolveSiteSeason } from '@src/theme/siteSeason';
import { useTheme } from '@src/theme/useTheme';
import { Typography } from '@src/components';

export function AccountTheme({
  account,
  onChange,
}: {
  account: UserAccountInfo;
  onChange: (account: UserAccountInfo) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const { setSeason } = useTheme();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const theme = resolveSiteSeason(user?.theme) as BoxTheme;
  const save = async (selected: BoxTheme) => {
    // The displayed default is not necessarily a saved account preference.
    if (busy || selected === user?.theme) return;
    setBusy(true);
    setError(false);
    try {
      const result = await santaClient.accountUpdateTheme({ theme: selected });
      if (!result.ok || !result.data) throw new Error('Theme save failed');
      onChange(result.data);
      await setSeason(selected);
      await dispatch(loadSession());
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error && (
        <Typography as="p" role="meta/error">
          {t('switcher.actionError')}
        </Typography>
      )}
      <AppearancePicker
        theme={theme}
        name={account.username || t('brand.name')}
        picture="santa"
        logo={null}
        disabled={busy}
        onThemeChange={(next) => void save(next)}
        selectionContext="profile"
        isPremium={hasPremiumFeature(user ?? {}, 'personalThemes')}
        showPictures={false}
      >
        {null}
      </AppearancePicker>
    </>
  );
}
